import React, {useEffect, useCallback, useMemo, useRef} from 'react';
import {Props} from './ac-tab-bar.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {THEMES, SIZES} from '@asdf-constants';

// Imports => Utilities
import {AcIsSet, AcIsFunction, AcIndicator} from '@asdf-utils';

// Imports => Atoms
import {AcRipple} from '@asdf-atoms';
import {AcSelectBox} from '../../atoms';

const _CLASSES = {
  MAIN: 'ac-tab-bar',
  WRP: 'ac-tab-bar-wrp',
  CHILDREN: 'ac-tab-bar__children',
  INDICATOR: {
    MAIN: 'ac-tab-bar__indicator',
    STATIC: 'ac-tab-bar__indicator--static',
    LEFT: 'ac-tab-bar__indicator--move-left',
    RIGHT: 'ac-tab-bar__indicator--move-right',
  },
  TAB: {
    MAIN: 'ac-tab',
    ACTIVE: 'ac-tab--active',
    LABEL: 'ac-tab__label',
    ICON: 'ac-tab__icon',
    NO_INDICATOR: 'ac-tab__no-indicator',
  },
  CENTER: 'ac-tab-bar__center',
};

const AcTabBar = ({
  tabs = [],
  active = null,
  callback = () => {},
  children,
  disableIndicator,
  selectForMobile,
  placeholder,
  selectMinWidth = '25rem',
  onlyDropdown = false,
  center = false,
  ...rest
}) => {
  const $container = useRef(null);
  const $indicator = useRef(null);
  const $tabs = useRef(null);
  const $tabIndicator = useRef(null);

  const $initTimeout = useRef(null);

  const initiate = () => {
    if (AcIsSet($initTimeout.current)) {
      clearTimeout($initTimeout.current);
    }

    let found = null;

    $initTimeout.current = setTimeout(() => {
      let n = 0;
      let len = tabs.length;

      for (n; n < len; n++) {
        const tab = tabs[n];

        if (tab.id !== active) {
          continue;
        } else if (tab.id === active && AcIsSet(tab.$ref)) {
          found = tab.$ref;
          if ($tabIndicator.current && $tabIndicator.current.recalculate) {
            $tabIndicator.current.recalculate(null, tab);
          }
          tab.$ref.click();
          break;
        }
      }
      if (!AcIsSet(found)) {
        initiate();
      }
    }, 1000 / 60);
  };

  const addEvents = () => {
    window.addEventListener('recalculateIndicator', initiate, {passive: true});
  };

  const removeEvents = () => {
    window.removeEventListener('recalculateIndicator', initiate, {passive: true});
  };

  useEffect(() => {
    addEvents();

    return () => removeEvents();
  }, []);

  useEffect(() => {
    if (AcIsSet(tabs)) {
      if (!AcIsSet($tabIndicator.current) && !disableIndicator) {
        $tabIndicator.current = new AcIndicator(
          $container,
          $indicator,
          tabs,
          _CLASSES.INDICATOR,
          false,
          true
        );

        if (AcIsSet($tabIndicator.current) && AcIsSet($tabIndicator.current.init)) {
          $tabIndicator.current.init().then(setTimeout(initiate, 1000 / 60));
        }
      } else if (AcIsSet($tabIndicator.current) && AcIsSet($tabIndicator.current.update)) {
        $tabIndicator.current.unload().then(instance => {
          $tabIndicator.current = instance.update(tabs);
          if (AcIsSet($tabIndicator.current) && AcIsSet($tabIndicator.current.init)) {
            $tabIndicator.current.init().then(setTimeout(() => initiate, 1000 / 60));
          }
        });
      }
    }

    return () => {
      if (AcIsSet($tabIndicator.current) && AcIsSet($tabIndicator.current.unload)) {
        $tabIndicator.current.unload().then(() => {
          $tabIndicator.current = null;
        });
      }
    };
  }, [$indicator, $container, tabs, $tabs]);

  const handleTabClick = useCallback(
    id => {
      if (callback) {
        callback(id);
      }
    },
    [callback]
  );

  const handleInputChange = (event, name, value, type, checked) => {
    handleTabClick(value);
  };

  const getLabelClassNames = useMemo(() => {
    return clsx(_CLASSES.TAB.LABEL);
  });

  const isActiveTab = useCallback(
    id => {
      return active && active === id;
    },
    [active]
  );

  const getTabClassNames = useCallback(
    id => {
      const is_active = isActiveTab(id);
      return clsx(
        _CLASSES.TAB.MAIN,
        is_active && _CLASSES.TAB.ACTIVE,
        disableIndicator && _CLASSES.TAB.NO_INDICATOR
      );
    },
    [isActiveTab]
  );

  const getIndicatorClassNames = useMemo(() => {
    return clsx(_CLASSES.INDICATOR.MAIN);
  });

  const getChildrenClassNames = useMemo(() => {
    return clsx(_CLASSES.CHILDREN);
  });

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  });

  const getWrpClassNames = useMemo(() => {
    return clsx(_CLASSES.WRP);
  });

  const getCenterClassNames = useMemo(() => {
    return clsx(center && _CLASSES.CENTER);
  }, [center]);

  const renderTabs = useMemo(() => {
    const collection = tabs;
    const len = collection.length;
    let n = 0;
    let result = [];
    let list = [];

    for (n; n < len; n++) {
      const item = collection[n];
      const {label, id, disabled} = item;

      const object = (
        <div
          key={`ac-tab-${id}`}
          ref={node => (item.$ref = node)}
          disabled={disabled}
          className={getTabClassNames(id)}
          onClick={() => handleTabClick(id)}
          role={'presentation'}
        >
          <AcRipple theme={THEMES.LIGHT} size={SIZES.SMALL} simple />
          {label && (
            <div
              className={getLabelClassNames}
              dangerouslySetInnerHTML={{
                __html: label,
              }}
              data-label={label}
              role={'tab'}
              aria-selected={isActiveTab(id)}
            />
          )}
        </div>
      );

      result.push(object);
      list.push(`ac-tab-${id}`);
    }

    $tabs.current = list;

    return result;
  }, [tabs, active, isActiveTab]);

  const renderSelect = useMemo(() => {
    const collection = tabs;
    const len = collection.length;
    let n = 0;
    let options = [];

    for (n; n < len; n++) {
      const item = collection[n];
      const {label, id, disabled} = item;
      if (id === 'EMPTY_OPTION') continue;
      options.push({name: label, value: id, disabled: disabled});
    }
    return (
      <AcSelectBox
        label={''}
        callback={handleInputChange}
        options={options}
        value={active}
        placeholder={placeholder}
      />
    );
  }, [tabs, active, isActiveTab]);

  const renderTabsOrSelect = useMemo(() => {
    if (onlyDropdown) {
      return <div style={{minWidth: selectMinWidth}}> {renderSelect}</div>;
    } else if (selectForMobile) {
      return (
        <>
          <div className={`d-md-flex d-none ${getCenterClassNames}`}>
            <>{renderTabs}</>
          </div>
          <div className={`d-md-none d-flex ${getCenterClassNames}`}>
            <div style={{minWidth: selectMinWidth}}>{renderSelect}</div>
          </div>
        </>
      );
    } else {
      return <>{renderTabs}</>;
    }
  }, [tabs, active, isActiveTab]);

  return (
    <div className={getWrpClassNames} {...rest}>
      <div className={getMainClassNames} ref={$container} role={'tablist'}>
        <span ref={$indicator} className={getIndicatorClassNames} />
        {renderTabsOrSelect}
        <div className={getChildrenClassNames}>{children}</div>
      </div>
    </div>
  );
};

AcTabBar.displayName = 'AcTabBar';
AcTabBar.propTypes = Props;

export {AcTabBar};
export default AcTabBar;
